import React, { useMemo, useCallback, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, MapPin, Users, Clock, AlertTriangle, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import { useSupabaseQuery, useSupabaseMutation } from '@/hooks/useSupabaseQuery';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { queryKeys } from '@/lib/query-client';
import type { Course } from "@/hooks/useCourses";
import { SuccessAnimation } from "@/components/ui/success-animation";
import { ScheduleTimeline } from "@/components/ui/schedule-timeline";
import { motion } from "framer-motion";
import { trackBookingComplete } from "@/lib/activity-tracker";

interface ScheduleItem {
  time: string;
  location: string;
  map_link?: string;
}

interface ScheduleTemplate {
  schedule_items: ScheduleItem[];
}

interface TripInfo {
  min_participants: number;
  current_participants: number;
  trip_status: string;
  bus_type: string | null;
}

interface BookingDetails {
  id: number;
  order_id: string;
  course: Course;
  booking_date: string;
  departure_date: string;
  departure_point: string | null;
  arrival_point: string | null;
  bus_type: string | null;
  tour_leader: string | null;
  total_amount: number;
  updated_at: string;
  schedule_template?: {
    schedule_items: ScheduleItem[];
  };
  participants: Array<{
    id: string;
    name: string;
    seat: number | null;
    participant_order: number;
  }>;
}

const BookingSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showAllSchedule, setShowAllSchedule] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { supabase } = useSupabaseConnection();  // 인증된 연결 사용
  
  // Extract URL parameters
  const paymentKey = searchParams.get("paymentKey");
  const orderId = searchParams.get("orderId");
  const amount = searchParams.get("amount");
  
  // 결제 처리 상태 추적 (무한루프 방지)
  const paymentProcessedRef = useRef(false);

  // Validation check for URL parameters
  const validationError = useMemo(() => {
    // booking ID는 필수
    if (!id) {
      return "예약 정보를 찾을 수 없습니다.";
    }
    // 결제 정보는 초기 결제 확인시에만 필요
    // 이미 확정된 예약은 결제 정보 없어도 OK
    return null;
  }, [id]);

  // Query to check existing booking status - try both booking_id and order_id
  const { data: existingBooking, error: bookingError, isLoading: isCheckingBooking } = useSupabaseQuery(
    queryKeys.bookings.detail(id || orderId || 'invalid'),
    async ({ signal }) => {
      console.log('🔍 예약 조회 시작:', { bookingId: id, orderId });
      
      let data = null;
      let error = null;
      
      // First try to find by booking_id if available
      if (id) {
        const bookingId = parseInt(id, 10);
        if (!isNaN(bookingId)) {
          const result = await supabase
            .from('bookings')
            .select(`
          booking_id,
          order_id,
          status,
          total_price,
          updated_at,
          trips!inner (
            trip_id,
            product_id,
            departure_date,
            departure_point,
            status,
            bus_type,
            products!inner (
              schedule_template,
              product_courses!inner (
                course_id,
                courses!inner (*)
              )
            )
          ),
          booking_participants (
            id,
            name,
            seat,
            participant_order
          )
            `)
            .eq('booking_id', bookingId)
            .abortSignal(signal!)
            .single();
            
          data = result.data;
          error = result.error;
          
          if (data) {
            console.log('✅ booking_id로 예약 찾음:', bookingId);
          }
        }
      }
      
      // If not found by booking_id and orderId exists, try by order_id
      if (!data && orderId) {
        const result = await supabase
          .from('bookings')
          .select(`
            booking_id,
            order_id,
            status,
            total_price,
            updated_at,
            trips!inner (
              trip_id,
              product_id,
              departure_date,
              departure_point,
              status,
              bus_type,
              products!inner (
                schedule_template,
                product_courses!inner (
                  course_id,
                  courses!inner (*)
                )
              )
            ),
            booking_participants (
              id,
              name,
              seat,
              participant_order
            )
          `)
          .eq('order_id', orderId)
          .abortSignal(signal!)
          .single();
          
        data = result.data;
        error = result.error;
        
        if (data) {
          console.log('✅ order_id로 예약 찾음:', orderId);
        }
      }

      if (!data) {
        console.error('❌ 예약을 찾을 수 없음:', { bookingId: id, orderId });
        if (error && error.code !== 'PGRST116') {
          throw new Error(`예약 조회 중 오류: ${error.message}`);
        }
        return null;
      }

      // 추가로 trip의 min_participants와 current_participants 정보 가져오기
      if (data) {
        const tripId = data.trips.trip_id;
        const busType = data.trips.bus_type;
        
        // bus_configurations에서 min_participants 가져오기
        const { data: busConfig, error: busError } = busType 
          ? await supabase
              .from('bus_configurations')
              .select('min_participants')
              .eq('bus_type', busType)
              .single()
          : { data: null, error: null };

        if (busError) {
          console.warn('버스 설정 조회 실패:', busError);
        }

        // 현재 trip의 총 참가자 수 계산
        const { data: tripStats, error: statsError } = await supabase
          .from('bookings')
          .select(`
            booking_participants(id)
          `)
          .eq('trip_id', tripId)
          .in('status', ['confirmed', 'pending']);

        if (statsError) {
          console.warn('참가자 통계 조회 실패:', statsError);
        }

        const currentParticipants = tripStats?.reduce((total, booking) => {
          return total + (booking.booking_participants?.length || 0);
        }, 0) || 0;

        // 결과에 추가 정보 포함
        return {
          ...data,
          trip_info: {
            min_participants: busConfig?.min_participants || 15, // 기본값
            current_participants: currentParticipants,
            trip_status: data.trips.status,
            bus_type: busType
          }
        };
      }

      return data;
    },
    {
      enabled: !validationError && (!!id || !!orderId),  // Enable when we have either id
      staleTime: 1000 * 60 * 5, // 5분
      retry: 2,
      debug: true
    }
  );

  // Mutation to confirm payment
  const confirmPaymentMutation = useSupabaseMutation(
    async (variables: { bookingId: string; paymentKey: string; orderId: string; amount: number }) => {
      console.log('결제 승인을 진행합니다.');
      
      const { data, error } = await supabase.functions.invoke("confirm-payment", {
        body: variables,
      });

      if (error) throw new Error(error.message);
      if (data.error) throw new Error(data.error);
      
      return data;
    },
    {
      debug: true,
      onSuccess: async (data) => {
        const booking = data.booking;
        console.log('✅ 결제 승인 성공!', {
          booking_id: booking.booking_id,
          order_id: booking.order_id,
          status: booking.status
        });
        
        // 예약 완료 활동 추적
        await trackBookingComplete(
          booking.booking_id,
          booking.order_id,
          booking.trips.product_id,
          booking.trips.trip_id
        );
        
        toast({
          title: "결제가 완료되었습니다!",
          description: "예약이 성공적으로 완료되었습니다."
        });
        
        // URL에서 결제 관련 파라미터 제거 (새로고침 시 중복 처리 방지)
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('orderId');
        newUrl.searchParams.delete('paymentKey');
        newUrl.searchParams.delete('amount');
        window.history.replaceState({}, document.title, newUrl.toString());
        console.log('🧹 URL 파라미터 정리 완료');
      },
      onError: (error) => {
        console.error('❌ 결제 승인 실패:', {
          error: error.message,
          bookingId: id,
          orderId: orderId
        });
        
        // 이미 처리된 결제인 경우 에러 토스트 표시하지 않음
        const errorMessage = error.message?.toLowerCase();
        if (errorMessage?.includes('already') || 
            errorMessage?.includes('confirmed') || 
            errorMessage?.includes('duplicate')) {
          console.log('ℹ️ 이미 처리된 결제입니다.');
          return;
        }
        
        // 실제 에러인 경우에만 토스트 표시
        toast({
          title: "결제 승인 실패",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  );


  // Transform booking data
  const bookingDetails = useMemo(() => {
    let sourceBooking;
    
    if (existingBooking && (existingBooking.status === 'confirmed' || existingBooking.status === 'pending')) {
      sourceBooking = existingBooking;
    } else if (confirmPaymentMutation.data?.booking) {
      sourceBooking = confirmPaymentMutation.data.booking;
    } else {
      return null;
    }
    
    const course = sourceBooking.trips.products.product_courses[0]?.courses;
    
    if (!course) {
      return null;
    }
    
    return {
      id: sourceBooking.booking_id,
      order_id: sourceBooking.order_id,
      course: course,
      booking_date: sourceBooking.trips.departure_date,
      departure_point: sourceBooking.trips.departure_point,
      total_amount: sourceBooking.total_price,
      updated_at: sourceBooking.updated_at,
      schedule_template: sourceBooking.trips.products.schedule_template,
      participants: sourceBooking.booking_participants || []
    };
  }, [existingBooking, confirmPaymentMutation.data]);

  // Process payment confirmation
  const processPayment = useCallback(async () => {
    console.log('🏁 processPayment 시작');
    console.log('📦 URL 파라미터:', {
      bookingId: id,
      orderId: orderId,
      paymentKey: paymentKey ? paymentKey.substring(0, 20) + '...' : 'missing',
      amount: amount
    });
    
    if (validationError || !id) {
      console.log('⚠️ processPayment 중단:', { validationError, hasBookingId: !!id });
      return;
    }
    
    console.log('🔄 processPayment 진행:', { 
      existingBooking: existingBooking ? {
        booking_id: existingBooking.booking_id,
        status: existingBooking.status,
        order_id: existingBooking.order_id,
        total_price: existingBooking.total_price
      } : '예약 없음',
      paymentProcessed: paymentProcessedRef.current
    });
    
    // Case 1: Booking already confirmed - redirect to booking detail
    if (existingBooking && existingBooking.status === 'confirmed') {
      console.log('✅ 예약이 이미 확정됨 - 예약 상세 페이지로 이동');
      // 결제 파라미터가 없으면 예약 상세 페이지로 리디렉션
      if (!paymentKey && !orderId && !amount) {
        navigate(`/booking/detail/${id}`);
        return;
      }
      // 결제 파라미터가 있지만 이미 확정된 경우 - URL 정리 후 표시
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('orderId');
      newUrl.searchParams.delete('paymentKey');
      newUrl.searchParams.delete('amount');
      window.history.replaceState({}, document.title, newUrl.toString());
      console.log('🧹 이미 확정된 예약 - URL 파라미터 정리');
      return;
    } 
    // Case 2: Booking pending - need to confirm payment
    if (existingBooking && existingBooking.status === 'pending' && !paymentProcessedRef.current) {
      console.log('💳 Pending 상태 예약 결제 승인 진행', {
        booking_id: existingBooking.booking_id,
        order_id: existingBooking.order_id,
        status: existingBooking.status
      });
      
      // Validate payment parameters
      if (!paymentKey || !orderId || !amount) {
        console.error('❌ 결제 파라미터 누락:', { 
          paymentKey: paymentKey || 'missing',
          orderId: orderId || 'missing',
          amount: amount || 'missing'
        });
        // 플래그를 설정하지 않고 return하여 다음 시도 가능하게 함
        return;
      }
      
      // 파라미터 검증이 통과한 후에만 플래그 설정
      paymentProcessedRef.current = true;
      
      console.log('🚀 confirm-payment Edge Function 호출 준비');
      console.log('📝 요청 데이터:', {
        bookingId: id,
        paymentKey: paymentKey.substring(0, 20) + '...',
        orderId: orderId,
        amount: Number(amount),
        expectedAmount: existingBooking.total_price
      });
      
      // 금액 검증
      if (Number(amount) !== existingBooking.total_price) {
        console.error('❌ 금액 불일치!', {
          urlAmount: Number(amount),
          dbAmount: existingBooking.total_price
        });
      }
      
      confirmPaymentMutation.mutate({
        bookingId: id,
        paymentKey: paymentKey,
        orderId: orderId,
        amount: Number(amount)
      });
    }
    // Case 3: No existing booking - should not happen in normal flow  
    else if (!existingBooking && !isCheckingBooking) {
      console.error('예약 정보를 찾을 수 없음');
    }
    // Case 4: Other cases - log for debugging
    else {
      console.log('⏭️ 결제 승인 건너뜀:', {
        hasBooking: !!existingBooking,
        status: existingBooking?.status,
        paymentProcessed: paymentProcessedRef.current
      });
    }
  }, [existingBooking, user, id, paymentKey, orderId, amount, validationError, isCheckingBooking, confirmPaymentMutation, navigate]);

  // Auto-trigger payment processing when data is ready
  React.useEffect(() => {
    if (!isCheckingBooking && !validationError && user) {
      processPayment();
    }
  }, [isCheckingBooking, validationError, user, processPayment]);

  // Loading state
  const isLoading = isCheckingBooking || confirmPaymentMutation.isPending;
  
  // Error state
  const error = validationError || 
    (bookingError ? `예약 조회 실패: ${bookingError.message}` : null) ||
    (confirmPaymentMutation.error ? `결제 승인 실패: ${confirmPaymentMutation.error.message}` : null) ||
    // pending 상태에서 결제 정보가 없는 경우
    (existingBooking && existingBooking.status === 'pending' && !paymentKey && !orderId && !amount ? 
      '결제 정보가 없습니다. 결제를 완료해주세요.' : null);

  // AuthProvider에서 전역적으로 탭 전환 문제를 해결하므로 로컬 솔루션 제거됨

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-8 sm:py-12">
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-brand mx-auto animate-spin mb-3" />
          <h2 className="text-base sm:text-lg font-bold text-gray-800">결제 승인 중...</h2>
          <p className="text-xs sm:text-sm text-gray-600">잠시만 기다려주세요.</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 sm:py-12">
          <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-2">오류 발생</h2>
          <p className="text-xs sm:text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>
        </div>
      );
    }

    if (bookingDetails) {
      // 투어 확정 여부 판단
      const tripInfo = existingBooking?.trip_info as TripInfo | undefined;
      const isTriplConfirmed = tripInfo && [
        'confirmed', 'ready', 'departed', 'in_progress', 'completed'
      ].includes(tripInfo.trip_status);

      return (
        <div className="space-y-3 sm:space-y-4">
          {/* 1순위: 예약 상태 - 컴팩트 디자인 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className={`border-l-4 shadow-sm ${
              isTriplConfirmed 
                ? 'border-l-success-500 bg-gradient-to-r from-success-50/50 to-white border-success-200' 
                : 'border-l-blue-500 bg-gradient-to-r from-blue-50/50 to-white border-blue-200'
            }`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                    isTriplConfirmed ? 'bg-success-100' : 'bg-blue-100'
                  }`}>
                    {isTriplConfirmed ? (
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-success-600" />
                    ) : (
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className={`text-lg sm:text-xl font-bold mb-1 ${
                      isTriplConfirmed ? 'text-success-700' : 'text-blue-700'
                    }`}>
                      {isTriplConfirmed ? '투어 확정' : '예약 접수 완료'}
                    </h2>
                    <p className={`text-xs sm:text-sm ${
                      isTriplConfirmed ? 'text-success-600' : 'text-blue-600'
                    }`}>
                      {isTriplConfirmed 
                        ? '예약 확인서가 이메일로 발송됩니다'
                        : '출발 5일 전(D-5)에 확정 안내드립니다'
                      }
                    </p>
                  </div>
                </div>
                
                {/* 참가자 현황 (미확정시에만) */}
                {!isTriplConfirmed && tripInfo && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-blue-700 font-medium">
                        현재 참가자
                      </span>
                      <span className="text-sm font-bold text-blue-800">
                        {tripInfo.current_participants}명 / {tripInfo.min_participants}명
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      최소 인원 미달 시 100% 환불
                    </p>
                  </div>
                )}
                
                {/* 결제 성공 축하 애니메이션 - 투어 확정 여부와 관계없이 표시 */}
                <SuccessAnimation
                  title=""
                  description=""
                  showConfetti={true}
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* 2순위: 핵심 투어 정보 - 한 눈에 보기 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid gap-3 sm:gap-4"
          >
            {/* 투어명 & 예약번호 */}
            <Card className="shadow-sm">
              <CardContent className="p-3 sm:p-4 text-center">
                <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2">
                  {bookingDetails.course.name}
                </h3>
                <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                  <span className="text-xs text-gray-500">예약번호</span>
                  <span className="text-xs font-medium text-gray-700">
                    {bookingDetails.order_id}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* 날짜 & 집결지 통합 카드 */}
            <Card className="shadow-sm">
              <CardContent className="p-3 sm:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* 날짜 정보 */}
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-4 h-4 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">출발일</p>
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {new Date(bookingDetails.booking_date).toLocaleDateString('ko-KR', { 
                          month: 'long', 
                          day: 'numeric',
                          weekday: 'short'
                        })}
                      </p>
                      <p className="text-xs text-gray-600">
                        {(bookingDetails.schedule_template as ScheduleTemplate)?.schedule_items?.[0]?.time || '추후 안내'}
                      </p>
                    </div>
                  </div>

                  {/* 집결지 정보 */}
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-success-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-success-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">집합장소</p>
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {bookingDetails.schedule_template?.schedule_items?.[0]?.location || 
                         bookingDetails.departure_point || 
                         bookingDetails.course.start_point || 
                         '추후 안내'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* 3순위: 참가자 & 결제 정보 통합 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid gap-3 sm:grid-cols-2 sm:gap-4"
          >
            {/* 참가자 정보 */}
            <Card className="shadow-sm">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-accent-600" />
                  <h4 className="text-sm font-semibold text-gray-800">
                    참가자 ({bookingDetails.participants.length}명)
                  </h4>
                </div>
                <div className="space-y-2">
                  {bookingDetails.participants.length > 0 ? (
                    bookingDetails.participants
                      .sort((a, b) => a.participant_order - b.participant_order)
                      .map((participant) => (
                        <div key={participant.id} className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-700 truncate flex-1 mr-2">
                            {participant.name}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                            {participant.seat ? `${participant.seat}번` : '좌석배정예정'}
                          </span>
                        </div>
                      ))
                  ) : (
                    <div className="text-xs text-gray-600">정보 로딩중...</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* 결제 정보 */}
            <Card className="shadow-md border-success-300 bg-gradient-to-br from-success-50 to-success-100/60 ring-1 ring-success-200">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-success-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="text-sm font-bold text-success-700">결제 완료</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">총 결제금액</span>
                    <span className="text-lg font-bold text-primary-600">
                      {bookingDetails.total_amount.toLocaleString()}원
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">결제일시</span>
                    <span className="text-base font-bold text-gray-800 whitespace-nowrap">
                      {new Date(bookingDetails.updated_at).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} {new Date(bookingDetails.updated_at).toLocaleTimeString('ko-KR', {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* 4순위: 투어 일정 - 축약 가능한 형태 */}
          {bookingDetails.schedule_template?.schedule_items && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="shadow-sm">
                <CardContent className="p-3 sm:p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-600" />
                    투어 일정
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(showAllSchedule ? bookingDetails.schedule_template.schedule_items : bookingDetails.schedule_template.schedule_items.slice(0, 3)).map((item, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm">
                        <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2 py-1 rounded min-w-0">
                          {item.time}
                        </span>
                        <span className="text-gray-700 truncate flex-1">
                          {item.location}
                        </span>
                      </div>
                    ))}
                    {bookingDetails.schedule_template.schedule_items.length > 3 && (
                      <button 
                        onClick={() => setShowAllSchedule(!showAllSchedule)}
                        className="text-xs text-primary hover:text-primary/80 transition-colors duration-200 w-full text-center py-2 hover:bg-primary/5 rounded"
                      >
                        {showAllSchedule 
                          ? '접기' 
                          : `+${bookingDetails.schedule_template.schedule_items.length - 3}개 일정 더보기`
                        }
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* 5순위: 안내사항 - 접을 수 있는 형태 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className={`shadow-sm border-l-4 ${
              isTriplConfirmed 
                ? 'border-l-success-400 bg-success-25' 
                : 'border-l-warning-400 bg-warning-25'
            }`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-semibold flex items-center gap-2 ${
                    isTriplConfirmed ? 'text-success-700' : 'text-warning-700'
                  }`}>
                    <AlertTriangle className="w-4 h-4" />
                    {isTriplConfirmed ? '출발 전 안내' : '예약 관련 안내'}
                  </h4>
                </div>
                
                {isTriplConfirmed ? (
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-success-500 rounded-full flex-shrink-0" />
                      <span>10분 전 집합</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-success-500 rounded-full flex-shrink-0" />
                      <span>신분증 필수</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-success-500 rounded-full flex-shrink-0" />
                      <span>일정 변경 가능</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-success-500 rounded-full flex-shrink-0" />
                      <span>상비약 지참</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-xs text-gray-700">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-warning-500 rounded-full flex-shrink-0" />
                      <span>미성사 시 전액 환불</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-warning-500 rounded-full flex-shrink-0" />
                      <span>확정/취소 시 이메일·문자 안내</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-warning-500 rounded-full flex-shrink-0" />
                      <span>출발 5일 전 최종 확정</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {(() => {
                const tripInfo = existingBooking?.trip_info as TripInfo | undefined;
                const isConfirmed = tripInfo && [
                  'confirmed', 'ready', 'departed', 'in_progress', 'completed'
                ].includes(tripInfo.trip_status);
                return isConfirmed ? '투어 확정 완료' : '예약 접수 완료';
              })()}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              예약이 성공적으로 처리되었습니다.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 메인 콘텐츠 영역 */}
            <div className="lg:col-span-2">
              {renderContent()}
            </div>
            
            {/* 사이드바 영역 */}
            <div className="lg:col-span-1 space-y-6">
              {bookingDetails && (
                <Card className="sticky top-6">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-primary" />
                      빠른 링크
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button 
                      onClick={() => navigate(`/booking/detail/${bookingDetails.id}`)}
                      className="w-full justify-start bg-primary hover:bg-primary/90"
                    >
                      예약 상세보기
                    </Button>
                    <Button 
                      onClick={() => navigate('/booking-history')}
                      className="w-full justify-start"
                      variant="outline"
                    >
                      예약 내역 전체보기
                    </Button>
                    <Button 
                      onClick={() => navigate('/')}
                      className="w-full justify-start"
                      variant="outline"
                    >
                      홈으로 돌아가기
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;